import { ActivatedRouteSnapshot, Resolve } from '@angular/router';
import { EMPTY, forkJoin, Observable, of } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { EbenFacade, TASK_ENDED_URL, TASK_NOT_FOUND_URL } from '@alf-nx-workspace/eben/data-access';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';

import { EbenTaskDetail } from '@alf-nx-workspace/eben/interfaces';
import { FormModel, ProcessVariableModel } from '@alfresco/adf-core';

export interface EbenTaskData {
  task: EbenTaskDetail | null;
  form: FormModel | null;
  variables: ProcessVariableModel[] | null;
  variableMap?: Record<string, unknown>;
}

const EMPTY_TASK_DATA: EbenTaskData = {
  task: null,
  form: null,
  variables: null,
  variableMap: {},
};

@Injectable({
  providedIn: 'root',
})
export class EbenTaskDataResolver implements Resolve<Observable<EbenTaskData>> {
  private readonly ebenFacade = inject(EbenFacade);

  resolve(route: ActivatedRouteSnapshot): Observable<EbenTaskData> {
    const taskId = route.params['taskId'];

    return this.ebenFacade.getTaskDetails(taskId).pipe(
      take(1),
      tap((task) => {
        if (!task) {
          this.ebenFacade.navigateToOutlet(TASK_NOT_FOUND_URL.replace(':taskId', taskId), true);
        }
      }),
      switchMap((task: EbenTaskDetail) => {
        if (task.endDate && !route.routeConfig?.path?.includes('task-ended')) {
          this.ebenFacade.navigateToOutlet(TASK_ENDED_URL.replace(':taskId', taskId), true);
          return EMPTY;
        }
        return (task ? this.resolveTaskData(taskId, task) : of(EMPTY_TASK_DATA))
      }),
      catchError((e) => {
        this.ebenFacade.navigateToOutlet(TASK_NOT_FOUND_URL.replace(':taskId', taskId), true);
        return EMPTY;
      })
    );
  }

  private resolveTaskData(taskId: string, task: EbenTaskDetail): Observable<EbenTaskData> {
    return forkJoin({
      task: of(task),
      form: this.ebenFacade.getTaskForm(taskId).pipe(catchError(() => of(null))),
      variables: this.ebenFacade.getTaskFormVariables(taskId).pipe(catchError(() => of(null))),
    }).pipe(
      map(({ task, form, variables }) => {
        const variableMap: Record<string, unknown> = {};
        if (variables) {
          variables.forEach((v) => {
            variableMap[v.id] = v.value;
          });
        }
        return {
          task,
          form,
          variables,
          variableMap,
        };
      })
    );
  }
}
